import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import { getDailyReport } from '../lib/api';
import { useActiveBranch } from '../lib/use-branch';
import { BranchPicker } from '../components/branch-picker';
import { todayStr, startOfLocalDay, endOfLocalDay } from '../lib/day';

/**
 * Daily Sales Report (feature 15). The selected day's LOCAL boundaries are
 * sent to the server so the report aligns with the tenant's day. Printable via
 * the shared `.receipt-tape` thermal stylesheet.
 */
export function DailyReportPage() {
  const { branchId, setSelectedBranchId, isScoped, branches } = useActiveBranch();
  const [date, setDate] = useState(todayStr());

  const from = startOfLocalDay(date);
  const to = endOfLocalDay(date);

  const query = useQuery({
    queryKey: ['pos-daily-report', branchId, date],
    enabled: !!branchId,
    queryFn: () =>
      getDailyReport({ branchId: branchId!, from: from.toISOString(), to: to.toISOString() }),
  });

  const r = query.data;
  const branchName = branches.find((b) => b.id === branchId)?.name ?? '';

  return (
    <Card>
      <CardHeader className="no-print flex flex-row items-center justify-between">
        <CardTitle>Daily sales report</CardTitle>
        <div className="flex items-end gap-2">
          <BranchPicker
            branches={branches}
            branchId={branchId}
            isScoped={isScoped}
            onChange={setSelectedBranchId}
          />
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <Button size="sm" onClick={() => window.print()} disabled={!r}>
            Print
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !r ? (
          <p className="text-sm text-muted-foreground">No data.</p>
        ) : (
          <div className="receipt-tape font-mono text-sm space-y-2 max-w-md">
            <div className="text-center space-y-0.5 pb-2 border-b border-dashed">
              <div className="font-semibold text-base">Daily Sales Report</div>
              <div className="text-xs">{branchName}</div>
              <div className="text-xs">{date}</div>
            </div>

            <div className="space-y-1">
              <Row label="Orders" value={String(r.ordersCount)} />
              <Row label="Gross sales" value={`₱${Number(r.grossSales).toFixed(2)}`} bold />
              <Row label="Total PV" value={Number(r.pvTotal).toFixed(2)} />
              <Row
                label="Refunds"
                value={`${r.refundsCount} · ₱${Number(r.refundsTotal).toFixed(2)}`}
              />
            </div>

            <div className="border-t border-dashed pt-2 space-y-1">
              <div className="text-xs font-semibold">PAYMENTS</div>
              {r.paymentBreakdown.length ? (
                r.paymentBreakdown.map((p) => (
                  <Row
                    key={p.method}
                    label={`${p.method} (${p.count})`}
                    value={`₱${Number(p.total).toFixed(2)}`}
                  />
                ))
              ) : (
                <div className="text-xs text-muted-foreground">No payments.</div>
              )}
            </div>

            {/* Cashier phase 5: money loaded into members' wallets at the till.
                Not sales, so it never enters the gross figure above. */}
            {r.walletTopups && r.walletTopups.length > 0 && (
              <div className="border-t border-dashed pt-2 space-y-1">
                <div className="text-xs font-semibold">WALLET TOP-UPS</div>
                {r.walletTopups.map((t) => (
                  <Row
                    key={t.method}
                    label={`${t.label} (${t.count})`}
                    value={`₱${Number(t.total).toFixed(2)}`}
                  />
                ))}
              </div>
            )}

            {/* What was given away to reach the gross figure above. Left off
                the tape entirely on a day with no discount of any kind. */}
            {r.discounts &&
              (Number(r.discounts.memberPricing) > 0 ||
                Number(r.discounts.coupons) > 0 ||
                Number(r.discounts.cashier) > 0) && (
                <div className="border-t border-dashed pt-2 space-y-1">
                  <div className="text-xs font-semibold">DISCOUNTS</div>
                  {Number(r.discounts.memberPricing) > 0 && (
                    <Row
                      label="Member pricing"
                      value={`₱${Number(r.discounts.memberPricing).toFixed(2)}`}
                    />
                  )}
                  {r.discounts.byLabel.map((d) => (
                    <Row
                      key={d.label}
                      label={`${d.label} (${d.count})`}
                      value={`₱${Number(d.total).toFixed(2)}`}
                    />
                  ))}
                  {Number(r.discounts.cashier) > 0 && (
                    <Row
                      label="Cashier discount"
                      value={`₱${Number(r.discounts.cashier).toFixed(2)}`}
                    />
                  )}
                </div>
              )}

            <div className="border-t border-dashed pt-2 space-y-1">
              <div className="text-xs font-semibold">TOP ITEMS</div>
              {r.topItems.length ? (
                r.topItems.map((it) => (
                  <Row
                    key={`${it.productId}:${it.variantName ?? ''}`}
                    label={`${it.variantName ? `${it.name} — ${it.variantName}` : it.name} ×${it.qty}`}
                    value={`₱${Number(it.total).toFixed(2)}`}
                  />
                ))
              ) : (
                <div className="text-xs text-muted-foreground">No items sold.</div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between ${bold ? 'font-semibold' : ''}`}>
      <span className="capitalize">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
